"use client";

import React, {useEffect, useState} from "react";
import Image from "next/image";
import Link from "next/link";
import {motion} from "framer-motion";
import {usePathname} from "next/navigation";

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/app/components/ui/accordion";
import {cn} from "@videohighlight/ui/cn";
import {Icons} from "@videohighlight/ui/icons";
import {LoginButton} from "@/components/login-button.tsx";
import {LogoIcon} from "./logo-icon.tsx";
import {SignupButton} from "@/components/signup-button.tsx";
import {SiteName} from "@/components/site-name.tsx";
import {SiteNameMultiRow} from "@/components/site-name-multi-row.tsx";

// Images
import researchView from "../../public/img/screenshots/vh-research-view-crop.png";
import {SiteNameSingleRow} from "@/components/site-name-single-row.tsx";

const listVariant = {
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.03,
        },
    },
    hidden: {
        opacity: 0,
    },
};

const itemVariant = {
    hidden: {opacity: 0},
    show: {opacity: 1},
};

export function Header() {
    let pathname = usePathname();

    if (!pathname) {
        pathname = "/";
    }

    const [isOpen, setOpen] = useState(false);
    const [showFeatures, setShowFeatures] = useState(false);
    const [hidden, setHidden] = useState(false);
    const lastPath = `/${pathname.split("/").pop()}`;

    useEffect(() => {
        const setPixelRatio = () => {
            const pixelRatio = window.devicePixelRatio || 1;
            document.documentElement.style.setProperty(
                "--pixel-ratio",
                `${1 / pixelRatio}`,
            );
        };

        setPixelRatio();
        window.addEventListener("resize", setPixelRatio);

        return () => window.removeEventListener("resize", setPixelRatio);
    }, []);

    const handleToggleMenu = () => {
        setOpen((prev) => {
            document.body.style.overflow = prev ? "" : "hidden";
            return !prev;
        });
    };

    const handleOnClick = () => {
        setShowFeatures(false);
        setHidden(true);

        setTimeout(() => {
            setHidden(false);
        }, 100);
    };

    const defaultIconClassNames: string = "w-5 h-5 text-foreground";

    const links = [
        {
            title: "Features",
            cover: (
                <Link href="/features" onClick={handleOnClick}>
                    <Image alt="Research View" src={researchView} quality={100} className={"rounded-md"}/>
                </Link>
            ),
            children: [
                {
                    path: "/features/summary",
                    title: "Summaries",
                    description: "Get a quick overview of the video using timestamped summaries.",
                    icon: <Icons.summary className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Key Points",
                    description: "Extract the key points and their location in the video.",
                    icon: <Icons.keyPoints className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Transcripts",
                    description: "Quickly transcribe the video content in any language.",
                    icon: <Icons.transcript className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Smart Search",
                    description: "Search for specific topics or keywords in the summary or transcript.",
                    icon: <Icons.smartSearch className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Chat & Video Q&A",
                    description: "Engage with the content using Q&A features.",
                    icon: <Icons.chat className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Highlights & Notes",
                    description: "Bookmark important sections and take notes. Think Kindle for videos.",
                    icon: <Icons.researchMode className={defaultIconClassNames}/>,
                    // icon: <Icons.highlight className="h-4 w-4 stroke-[1.5px]"/>,
                },
                {
                    path: "/",
                    title: "Playlists",
                    description: "Organize videos into collections.",
                    icon: <Icons.playlist className={defaultIconClassNames}/>,
                },
                {
                    path: "/",
                    title: "Export",
                    description: "Export summaries and highlights to Notion or Readwise.",
                    icon: <Icons.notion className={defaultIconClassNames}/>,
                },
                // {
                //     path: "/features",
                //     title: "Video Search",
                //     description: "Search for videos based on content.",
                //     // icon: <Icons.search className={"w-5 h-5 text-foreground"} />,
                //     icon: <SearchVisualIcon size={20}/>,
                // },
                {
                    path: "/",
                    title: "Video Alerts",
                    description: "Google Alerts, but for videos.",
                    icon: <Icons.alerts className={defaultIconClassNames}/>,
                },
            ],
        },
        {
            title: "Mission",
            path: "/mission",
        },
        // {
        //     title: "What's new?",
        //     path: "/updates",
        // },
        {
            title: "Pricing",
            path: "/pricing",
        },
        // {
        //     title: "Developers",
        //     cover: (
        //         <Link href="/engine" onClick={handleOnClick}>
        //             <Image alt="Engine" src={menuEngine} quality={100}/>
        //         </Link>
        //     ),
        //     children: [
        //         {
        //             path: "https://docs.videohighlight.com",
        //             title: "Documentation",
        //             icon: <MdOutlineDescription size={20}/>,
        //         },
        //         {
        //             title: "Join the community",
        //             path: "https://go.videohighlight.com/anPiuRx",
        //             icon: <FaDiscord size={19}/>,
        //         },
        //         {
        //             title: "Apps & Integrations",
        //             path: "https://docs.videohighlight.com",
        //             icon: <MdOutlineIntegrationInstructions size={20}/>,
        //         },
        //     ],
        // },
    ];

    if (pathname.includes("pitch")) {
        return null;
    }

    return (
        <header>
            {/* 0. Mask Blur Overlay (z-10) */}
            <div className={cn(
                "header-mask",
                "fixed top-0 left-0",
                "w-full h-[80px]",  // this goes behind the header to blur the scrolling page content going behind the header
                "z-10",             // above the page content, below the header
                "pointer-events-none"
            )}></div>

            {/* 1. Wrapper for the navigation bar + the extended feature links */}
            <div
                className={cn(
                    "fixed top-4 left-0 right-0 flex flex-col items-center",
                    "px-2 md:px-4 min-w-[300px] max-w-[1056px] mx-auto",
                    "z-50",         // highest z-index
                    (pathname === "/") && "ease-in-out transition-all duration-300 animate-header-slide-down-fade",
                )}
                style={{
                    transitionProperty: "backdrop-blur-xl, background-color",  // Exclude border from opacity transitions
                }}
            >
                {/* 1.2. Inner wrapper for the navigation bar and the features dropdown, to apply a uniform blur effect*/}
                <div className={cn(
                    "rounded-xl",
                    showFeatures ? "bg-[#02020360] backdrop-blur-xl" : "",
                    // showFeatures ? "bg-neutral-900/50 backdrop-blur-xl" : "",
                )}>

                    {/* 1.2.1. Div handling the border and background */}
                    <div className={cn(
                        "w-full max-w-[1056px]",
                        "border border-white/[0.07]",
                        "rounded-t-[14px]",
                        "z-30",
                        showFeatures ? "border-b-transparent" : "rounded-b-[14px] backdrop-blur-xl",
                        )}
                         style={{
                             transitionProperty: "backdrop-blur-xl, background-color",  // Exclude border from opacity transitions
                         }}
                    >
                        {/* 1.2.1.1. Navigation Bar */}
                        <nav className={cn("h-[48px]",
                            "space-x-4 px-2.5 flex items-center",
                            "justify-between",
                        )}>

                            {/* 1.2.1.1.1. Logo + Site Name */}
                            <Link href="/" className="flex items-center">
                                {/* 1.1.1. Logo */}
                                {/*<LogoIcon className={"hidden sm:flex"} width={28} height={28}/>*/}
                                {/* 1.1.2. Site Name */}
                                <SiteNameMultiRow className={"flex text-sm ml-1.5"}/> {/* ml-2 */}
                                {/*<SiteNameSingleRow className={"flex text-sm ml-1.5"}/> /!* ml-2 *!/*/}
                                {/*<SiteName className={"flex ml-2 text-md"}/>*/}
                            </Link>

                            {/* 1.2.1.1.2. Navigation Links */}
                            <ul className="space-x-2 text-sm hidden md:flex mx-3">
                                {links.map(({path, title, children, cover}) => {

                                    // if we have the path parameter defined, we have a normal link
                                    if (path) {
                                        return (
                                            <li key={title} className={"flex items-center"}>
                                                <Link
                                                    onClick={handleOnClick}
                                                    href={path}
                                                    className="h-[48px] items-center justify-center text-sm px-3 py-2 inline-flex text-secondary-foreground transition-opacity hover:text-foreground duration-200 font-semibold"
                                                >
                                                    {title}
                                                </Link>
                                            </li>
                                        );
                                    }

                                    // if we don't hav the path defined, we have children (e.g. the Features dropdown)
                                    return (
                                        <li
                                            key={title}
                                            className={"group flex items-center"}
                                            onMouseEnter={() => setShowFeatures(true)}
                                            onMouseLeave={() => setShowFeatures(false)}
                                        >
                                    <span
                                        className="h-[48px] items-center justify-center text-sm transition-opacity duration-200 px-3 py-8 inline-flex text-secondary-foreground cursor-pointer">
                                      {title}
                                    </span>
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* 1.2.1.1.3. Button Group (Login, Sign up, Mobile Menu) - Grouped so they can float to the right */}
                            <div className={"flex space-x-4 items-center"}>
                                {/* 1.2.1.1.3.1. Login Button */}
                                <LoginButton/>

                                {/* 1.2.1.1.3.2. Sign up Button */}
                                <SignupButton variant={"homepage"}/>

                                {/* 1.2.1.1.3.3. Mobile Menu Button */}
                                <button
                                    type="button"
                                    className="md:hidden p-2"
                                    onClick={() => handleToggleMenu()}
                                >
                                    <Icons.mobileMenu className="h-[1.1rem] w-[1.1rem] stroke-[1.5px] text-foreground"/>
                                    <span className="sr-only">Toggle Menu</span>
                                </button>
                            </div>
                        </nav>
                    </div>

                    {/* 1.2.2. Features Dropdown (this is at the same level as the wrapper for the nav */}
                    <div
                        className="relative"
                        onMouseEnter={() => setShowFeatures(true)}
                        onMouseLeave={() => setShowFeatures(false)}>
                        <div
                            className={cn(
                                "flex",
                                "w-full max-w-[1056px]",
                                "",
                                // "shadow-lg",
                                "gap-4",                         // Controls height extension on hover
                                "overflow-hidden transition-all duration-300 ease-in-out",      // animate height
                                "border-b border-x border-white/[0.07]",   // make top border disappear
                                "rounded-b-xl",
                                "z-30",
                                showFeatures ? "h-[400px] opacity-100" : "h-0 opacity-0",
                            )}
                            style={{
                                transitionProperty: "height, background-color, opacity, backdrop-blur-md",  // Exclude border from opacity transitions
                            }}
                        >
                            {/* 1.2.2.1. Feature Grid */}
                            <div className="p-3 grid grid-cols-3 gap-x-3 gap-y-3 text-secondary-foreground h-full">
                                {links
                                    .find(link => link.title === "Features")
                                    ?.children?.map(child => (
                                            <Link
                                                key={child.title}
                                                onClick={handleOnClick}
                                                href={child.path}
                                                className={cn(
                                                    "flex flex-col relative overflow-hidden group",
                                                    "h-full p-6 rounded-xl",
                                                    "text-neutral-300",
                                                    "bg-neutral-900/40 shine",
                                                    "hover:brightness-125 transition-all", // hover:scale-100
                                                    "cursor-pointer")}
                                            >
                                                <div
                                                    className="relative z-10 h-full flex flex-col justify-between">
                                                    <div className="space-y-2">
                                                        <p className="flex flex-row items-start gap-2 text-sm font-medium">
                                                            {child.icon}{" "}
                                                            {child.title}
                                                        </p>
                                                        <p className="text-xs text-neutral-400">{child.description}</p>
                                                    </div>
                                                    {/*<div className="mt-auto space-y-2">*/}
                                                    {/*    <p className="text-xs text-neutral-500">*/}
                                                    {/*        {"extra"}*/}
                                                    {/*    </p>*/}
                                                    {/*</div>*/}
                                                </div>
                                            </Link>
                                        )
                                    )}
                            </div>
                        </div>
                    </div>

                </div>


                {/* 1.3. Mobile Menu (z-40) */}
                {isOpen && (
                    <motion.div
                        className={cn("fixed top-4 right-0 left-0 bottom-0 h-screen min-w-[300px] z-40",
                            "px-3 mx-2 border border-white/[0.07]",
                            "bg-dark-blue rounded-[14px] shadow-lg")}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                    >
                        {/* 1.3.1. Logo and Site Name and Close Button */}
                        <div className="flex justify-between relative">
                            {/* 1.3.1.1. Logo and Site Name */}
                            <button type="button" onClick={handleToggleMenu}>
                                <div className={"flex items-center py-[10px]"}>
                                    {/* 1.1.1. Logo */}
                                    {/*<LogoIcon width={26} height={26} className={"hidden sm:flex"}/>*/}
                                    {/* 1.1.2. Site Name */}
                                    {/*<SiteName className={"hidden sm:flex ml-2"}/>*/}
                                    <SiteNameMultiRow className={"flex text-sm ml-1"}/> {/* ml-2 */}
                                </div>
                            </button>

                            {/* 1.3.1.2. Close Button */}

                            {/* 1.3. Button Group (Login, Sign up, Mobile Menu) - Grouped so they can float to the right */}
                            <div className={"flex space-x-4 items-center"}>
                                {/* 1.3.1. Login Button */}
                                <LoginButton/>

                                {/* 1.3.2. Sign up Button */}
                                <SignupButton variant={"homepage"}/>

                                {/* 1.3.3. Mobile Menu Button */}
                                <button
                                    type="button"
                                    className="ml-auto md:hidden px-[7px]"
                                    onClick={handleToggleMenu}
                                >
                                    <Icons.cross className="h-[1.1rem] w-[1.1rem] stroke-[1.5px] text-foreground"/>
                                    <span className="sr-only">Close Menu</span>
                                </button>
                            </div>
                        </div>

                        <div className="h-screen pb-[150px] overflow-auto">
                            <motion.ul
                                initial="hidden"
                                animate="show"
                                className="px-3 pt-8 text-xl text-base space-y-8 mb-8 overflow-auto"
                                variants={listVariant}
                            >
                                {links.map(({path, title, children}) => {
                                    const isActive =
                                        path === "/updates"
                                            ? (pathname != null && pathname.includes("updates"))
                                            : path === lastPath;

                                    if (path) {
                                        return (
                                            <motion.li variants={itemVariant} key={`mobile-${title}`}>
                                                <Link
                                                    href={path}
                                                    className={cn("text-muted-foreground hover:text-foreground hover:bg-muted",
                                                        isActive && "text-primary")}
                                                    onClick={handleToggleMenu}
                                                >
                                                    {title}
                                                </Link>
                                            </motion.li>
                                        );
                                    }

                                    return (
                                        <li key={`mobile-${title}`}>
                                            <Accordion collapsible type="single">
                                                <AccordionItem value="item-1" className="border-none">
                                                    <AccordionTrigger
                                                        orientation={"horizontal"}
                                                        className="flex items-center justify-between w-full font-normal p-0 hover:no-underline text-muted-foreground hover:text-foreground">
                                                    <span
                                                        className="text-muted-foreground hover:text-foreground">{title}</span>
                                                    </AccordionTrigger>

                                                    {children && (
                                                        <AccordionContent className="text-xl">
                                                            <ul className="space-y-8 ml-4 mt-6"
                                                                key={`${path}-${title}`}>
                                                                {children.map((child) => {
                                                                    return (
                                                                        <li key={`${child.path}-${child.title}`}>
                                                                            <Link
                                                                                onClick={handleToggleMenu}
                                                                                href={child.path}
                                                                                className="text-muted-foreground hover:text-foreground"
                                                                            >
                                                                                {child.title}
                                                                            </Link>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </AccordionContent>
                                                    )}
                                                </AccordionItem>
                                            </Accordion>
                                        </li>
                                    );
                                })}
                            </motion.ul>
                        </div>
                    </motion.div>
                )}
            </div>
        </header>
    );
}
